import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, ErrorBox, Field, Panel, ScreenHeading, Tag, type TagTone } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { dateTime, logPct } from '../../lib/format'
import { useT } from '../../lib/i18n'
import type { VersionDetail, VersionSummary } from '../../lib/types'

export const VERSION_TONE: Record<string, TagTone> = { active: 'pos', draft: 'info', superseded: 'neutral', rolled_back: 'warn' }

interface DiffEntry {
  driverId: string
  driverName: string
  block: string
  kind: string
  level: string
  from: number | null
  to: number | null
  fromPct: number | null
  toPct: number | null
}
interface Diff {
  from: string
  to: string
  effects: DiffEntry[]
  other: { path: string; from: unknown; to: unknown }[]
}

/** §8.5 — list, import, open a draft, diff two versions, roll back with a reason. */
export function Versions() {
  const navigate = useNavigate()
  const { t } = useT()
  const [rows, setRows] = useState<VersionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [json, setJson] = useState('')
  const [showImport, setShowImport] = useState(false)
  const [diffFrom, setDiffFrom] = useState('')
  const [diffTo, setDiffTo] = useState('')
  const [diff, setDiff] = useState<Diff | null>(null)
  const [rollbackId, setRollbackId] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const reload = useCallback(() => api.get<VersionSummary[]>('/api/model-versions').then(setRows).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    reload()
  }, [reload])

  const active = rows?.find((r) => r.status === 'active')

  const openDraft = async () => {
    try {
      const d = await api.post<VersionDetail>('/api/model-versions/draft', {})
      navigate(`/admin/versions/${d.summary.id}`)
    } catch (e) {
      setError(errorMessage(e))
    }
  }
  const doImport = async () => {
    setError(null)
    try {
      const payload = JSON.parse(json)
      const d = await api.post<VersionDetail>('/api/model-versions/import', payload)
      setShowImport(false)
      setJson('')
      navigate(`/admin/versions/${d.summary.id}`)
    } catch (e) {
      setError(e instanceof SyntaxError ? `Not valid JSON: ${e.message}` : errorMessage(e))
    }
  }
  const downloadTemplate = async () => {
    setError(null)
    try {
      const p = await api.get<unknown>('/api/model-versions/template')
      const url = URL.createObjectURL(new Blob([JSON.stringify(p, null, 2)], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'model_template.json'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(errorMessage(e))
    }
  }
  const loadDiff = async () => {
    if (!diffFrom || !diffTo) return
    setError(null)
    try {
      setDiff(await api.get<Diff>(`/api/model-versions/diff?from=${diffFrom}&to=${diffTo}`))
    } catch (e) {
      setError(errorMessage(e))
    }
  }
  const doRollback = async () => {
    if (!rollbackId) return
    setError(null)
    try {
      await api.post(`/api/model-versions/${rollbackId}/rollback`, { reason })
      setRollbackId(null)
      setReason('')
      await reload()
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const short = (id: string) => id.slice(0, 8)
  const label = (v: VersionSummary) => `${short(v.id)} · ${t(v.status)} · ${v.note || t('(no note)')}`

  return (
    <div>
      <ScreenHeading
        title={t('Model versions')}
        right={
          <div className="flex gap-2">
            <button type="button" className="btn" onClick={() => setShowImport((s) => !s)}>
              {t('Import payload')}
            </button>
            <button type="button" className="btn btn-primary" onClick={openDraft} disabled={!active}>
              {t('Open draft from active')}
            </button>
          </div>
        }
      />
      {error && <div className="mb-4"><ErrorBox>{error}</ErrorBox></div>}

      {showImport && (
        <Panel
          className="mb-4"
          title={t('Import a model')}
          right={
            <div className="flex gap-2">
              <button
                type="button"
                className="btn !py-0.5 text-xs"
                title={t('The template is the active model, or all weights at zero when none is active. Change the weights and policy, then import it here. It arrives as a draft to review and publish.')}
                onClick={downloadTemplate}
              >
                {t('Download template')}
              </button>
              <label className="btn !py-0.5 text-xs cursor-pointer">
                {t('Choose file')}
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) f.text().then(setJson)
                    e.target.value = ''
                  }}
                />
              </label>
            </div>
          }
        >
          <textarea className="field font-mono text-xs" rows={10} value={json} onChange={(e) => setJson(e.target.value)} placeholder='{ "schemaVersion": 1, … }' />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className="btn" onClick={() => setShowImport(false)}>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={doImport} disabled={!json.trim()}>
              {t('Validate and import')}
            </button>
          </div>
        </Panel>
      )}

      <Panel>
        <DataTable
          rows={rows ?? []}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/admin/versions/${r.id}`)}
          columns={[
            { key: 'st', header: t('Status'), render: (r) => <Tag tone={VERSION_TONE[r.status] ?? 'neutral'}>{t(r.status)}</Tag> },
            { key: 'id', header: t('Version'), render: (r) => <span className="font-mono text-xs text-slate-400">{short(r.id)}</span> },
            { key: 'prov', header: t('Provenance'), render: (r) => (
              <span className="text-xs">
                {r.source === 'imported' ? <Tag tone="info">{t('imported')}</Tag> : <Tag tone="warn">{t('hand-edited')}</Tag>}
                {r.parentVersionId && <span className="ml-1.5 text-slate-500">{t('from')} {short(r.parentVersionId)}</span>}
                {r.editedEffects + r.editedOther > 0 && <span className="ml-1.5 text-slate-400">· {t('{n} weight(s) moved', { n: r.editedEffects })}{r.editedOther ? `, ${t('{n} other', { n: r.editedOther })}` : ''}</span>}
              </span>
            ) },
            { key: 'basis', header: t('Basis'), render: (r) => <span className="text-xs text-slate-400">{r.basis === 'delivered' ? t('delivered') : t('ex works')}</span> },
            { key: 'drv', header: t('Drivers'), align: 'right', render: (r) => r.driverCount },
            { key: 'pub', header: t('Published'), render: (r) => <span className="text-xs text-slate-400">{r.publishedAt ? `${dateTime(r.publishedAt)} · ${r.publishedByName ?? ''}` : `${t('created')} ${dateTime(r.createdAt)}`}</span> },
            { key: 'note', header: t('Note'), className: 'max-w-[22rem] truncate', render: (r) => <span title={r.note}>{r.note || <span className="text-slate-600">–</span>}</span> },
            { key: 'act', header: '', align: 'right', render: (r) => (
              r.status === 'superseded' || r.status === 'rolled_back' ? (
                <button type="button" className="btn !py-0.5 text-xs" onClick={(e) => { e.stopPropagation(); setRollbackId(r.id) }}>
                  {t('Roll back to this')}
                </button>
              ) : null
            ) },
          ]}
        />
      </Panel>

      {rollbackId && (
        <Panel className="mt-4" title={`${t('Roll back to')} ${short(rollbackId)}`} subtitle={t('The current active version is retired and this version’s ratings return. A reason is required and audited.')}>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('Reason')}>
              <input className="field w-[28rem] max-w-full" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <button type="button" className="btn btn-danger" disabled={!reason.trim()} onClick={doRollback}>
              {t('Roll back')}
            </button>
            <button type="button" className="btn" onClick={() => setRollbackId(null)}>
              {t('Cancel')}
            </button>
          </div>
        </Panel>
      )}

      <Panel className="mt-4" title={t('Compare two versions')}>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('From')}>
            <select className="field w-80" value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)}>
              <option value="">{t('Choose…')}</option>
              {rows?.map((v) => (
                <option key={v.id} value={v.id}>{label(v)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('To')}>
            <select className="field w-80" value={diffTo} onChange={(e) => setDiffTo(e.target.value)}>
              <option value="">{t('Choose…')}</option>
              {rows?.map((v) => (
                <option key={v.id} value={v.id}>{label(v)}</option>
              ))}
            </select>
          </Field>
          <button type="button" className="btn" onClick={loadDiff} disabled={!diffFrom || !diffTo}>
            {t('Compare')}
          </button>
        </div>
        {diff && (
          <div className="mt-4 space-y-3">
            {diff.effects.length === 0 && diff.other.length === 0 && <p className="text-xs text-slate-500">{t('No differences.')}</p>}
            {diff.effects.length > 0 && (
              <DataTable
                rows={diff.effects}
                rowKey={(e) => `${e.driverId}|${e.level}`}
                columns={[
                  { key: 'd', header: t('Driver'), render: (e) => <span className="text-slate-200">{e.driverName}</span> },
                  { key: 'b', header: t('Block'), render: (e) => <Tag>{e.block}</Tag> },
                  { key: 'l', header: t('Level'), render: (e) => e.level },
                  { key: 'f', header: t('From'), align: 'right', render: (e) => (e.from === null ? <span className="text-slate-600">{t('absent')}</span> : e.kind === 'relative' ? logPct(e.from) : `${e.from} €`) },
                  { key: 't', header: t('To'), align: 'right', render: (e) => (e.to === null ? <span className="text-slate-600">{t('removed')}</span> : e.kind === 'relative' ? logPct(e.to) : `${e.to} €`) },
                  { key: 'm', header: t('Move'), align: 'right', render: (e) => {
                    const f = e.from ?? 0, t = e.to ?? 0
                    const d = e.kind === 'relative' ? ((e.toPct ?? 0) - (e.fromPct ?? 0)) : t - f
                    return <span className={d > 0 ? 'text-emerald-400' : d < 0 ? 'text-rose-400' : 'text-slate-400'}>{d > 0 ? '+' : ''}{d.toFixed(2)} {e.kind === 'relative' ? 'pp' : '€'}</span>
                  } },
                ]}
              />
            )}
            {diff.other.length > 0 && (
              <DataTable
                rows={diff.other}
                rowKey={(o) => o.path}
                columns={[
                  { key: 'p', header: t('Setting'), render: (o) => <span className="font-mono text-xs text-slate-300">{o.path.replace(' ', ' › ')}</span> },
                  { key: 'f', header: t('From'), align: 'right', render: (o) => <span className="font-mono text-xs">{JSON.stringify(o.from ?? null)}</span> },
                  { key: 't', header: t('To'), align: 'right', render: (o) => <span className="font-mono text-xs">{JSON.stringify(o.to ?? null)}</span> },
                ]}
              />
            )}
          </div>
        )}
      </Panel>
    </div>
  )
}
